import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Navigation, Heart, Package, Users, Truck, Home, Stethoscope, HelpCircle } from "lucide-react";
import { renderToStaticMarkup } from "react-dom/server";

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const URGENCY_COLORS = {
  urgente: "#f64e0e",
  prioridad: "#fa9000",
  activo: "#686af3",
  info: "#a88efb",
};

const TYPE_CONFIG = {
  material: { label: "Material", icon: Package },
  voluntarios: { label: "Voluntarios", icon: Users },
  transporte: { label: "Transporte", icon: Truck },
  acogida: { label: "Acogida", icon: Home },
  veterinario: { label: "Veterinario", icon: Stethoscope },
  otro: { label: "Otro", icon: HelpCircle },
};

function createCustomIcon(color, type) {
  const TypeIcon = TYPE_CONFIG[type]?.icon || MapPin;
  const svg = renderToStaticMarkup(
    <div style={{
      width: 36, height: 36, borderRadius: "50% 50% 50% 0",
      background: color, transform: "rotate(-45deg)",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    }}>
      <div style={{ transform: "rotate(45deg)", color: "white", fontSize: 16 }}>
        <MapPin size={16} color="white" />
      </div>
    </div>
  );

  return L.divIcon({
    html: `<div style="width:36px;height:36px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3)"><div style="transform:rotate(45deg);color:white;display:flex;align-items:center;justify-content:center"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg></div></div>`,
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
}

function createUserIcon() {
  return L.divIcon({
    html: `<div style="width:20px;height:20px;border-radius:50%;background:#686af3;border:3px solid white;box-shadow:0 2px 8px rgba(104,106,243,0.5)"></div>`,
    className: "",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function FlyToLocation({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 12, { duration: 1.2 });
    }
  }, [position, map]);
  return null;
}

export default function NeedsMap({ needs, emergencies = [], onOfferSent }) {
  const { user } = useAuth();
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);

  const handleGeolocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = [pos.coords.latitude, pos.coords.longitude];
        setUserLocation(loc);
        setFlyTarget(loc);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Find map center: user location > first need with coords > Spain center
  const getCenter = () => {
    if (userLocation) return userLocation;
    const withCoords = needs.find((n) => n.location_lat && n.location_lng);
    if (withCoords) return [withCoords.location_lat, withCoords.location_lng];
    return [39.5, -3.0]; // Spain center
  };

  const needsWithCoords = needs.filter((n) => n.location_lat && n.location_lng);

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg" data-testid="needs-map">
      {/* Geolocate button */}
      <div className="absolute top-4 right-4 z-[1000]">
        <Button
          onClick={handleGeolocate}
          disabled={locating}
          size="sm"
          className="bg-white text-[var(--navy)] hover:bg-[var(--cream)] shadow-md rounded-full px-4 font-body text-xs"
          data-testid="geolocate-btn"
        >
          <Navigation className={`w-4 h-4 mr-1.5 ${locating ? "animate-spin" : ""} text-[var(--blue-vibrant)]`} />
          {locating ? "Localizando..." : "Mi ubicación"}
        </Button>
      </div>

      <MapContainer
        center={getCenter()}
        zoom={6}
        style={{ height: "500px", width: "100%" }}
        className="z-0"
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
        />

        {flyTarget && <FlyToLocation position={flyTarget} />}

        {/* User location */}
        {userLocation && (
          <>
            <Marker position={userLocation} icon={createUserIcon()}>
              <Popup>
                <div className="text-center p-1">
                  <p className="font-heading text-sm font-bold text-[var(--navy)]">Tu ubicación</p>
                </div>
              </Popup>
            </Marker>
            <Circle
              center={userLocation}
              radius={5000}
              pathOptions={{ color: "#686af3", fillColor: "#686af3", fillOpacity: 0.08, weight: 1 }}
            />
          </>
        )}

        {/* Need markers */}
        {needsWithCoords.map((need) => {
          const color = URGENCY_COLORS[need.urgency] || "#686af3";
          const typeLabel = TYPE_CONFIG[need.type]?.label || need.type;

          return (
            <Marker
              key={need.need_id}
              position={[need.location_lat, need.location_lng]}
              icon={createCustomIcon(color, need.type)}
            >
              <Popup maxWidth={280}>
                <div className="p-1" style={{ minWidth: 200 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                      style={{ backgroundColor: `${color}18`, color }}
                    >
                      {need.urgency}
                    </span>
                    <span className="text-[10px] text-slate-400">{typeLabel}</span>
                  </div>
                  <p className="font-bold text-sm text-[#29294d] mb-1">{need.title}</p>
                  <p className="text-xs text-slate-500 mb-2 line-clamp-2">{need.description}</p>
                  {need.quantity && (
                    <p className="text-xs text-[#686af3] mb-2" style={{ fontFamily: "monospace" }}>
                      Cantidad: {need.quantity}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-400">
                      {need.shelter_name} &middot; {need.offers_count || 0} ofertas
                    </span>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] glass rounded-xl p-3 shadow-md">
        <div className="flex items-center gap-3 text-[10px]">
          {Object.entries(URGENCY_COLORS).map(([key, color]) => (
            <div key={key} className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-slate-500 capitalize">{key}</span>
            </div>
          ))}
          {userLocation && (
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-[var(--blue-vibrant)] border-2 border-white" />
              <span className="text-slate-500">Tu</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
